// src/components/ui-components/headerBreadcrumbs.js
// Central route → breadcrumb trail for the 3-row header's row-1 top line.
// Mirrors headerTabs.js in shape (an if-ladder over the pathname). The first
// crumb is the sidebar section; the last crumb is the current page (no url) and
// doubles as the derived page title when a page injects no title slot. Detail
// routes append the record name via opts.recordName, falling back to a generic
// label when the page hasn't declared one (pages feed it via useSetHeaderRecordName).
// Unmapped routes return [] (the breadcrumb row hides and the title derives from
// a lone tab label instead).

const seg = (pathname, i) => pathname.split("/")[i];

export function getHeaderBreadcrumbs(location, params = {}, opts = {}) {
  const path = location.pathname;
  const rec = opts.recordName || null;

  // ---------------- Control ----------------
  if (
    path === "/financial-control" ||
    path === "/budget-enforcement" ||
    path === "/ownership-accountability" ||
    path === "/forecast-predictability"
  ) {
    return [{ label: "Control" }, { label: "Control Center" }];
  }

  // ---------------- Control ----------------
  // Cost & Usage Explorer + AI Usage Monitor are two tabs of the "Cloud & AI Spend"
  // section (under the Control sidebar group); both breadcrumb to Control / Cloud &
  // AI Spend, and the derived page title is "Cloud & AI Spend" (the tab strip names
  // the active view).
  if (path === "/cost-analyzer" || path === "/ai-monitor") {
    return [{ label: "Control" }, { label: "Cloud & AI Spend" }];
  }

  // ---------------- Control ----------------
  // Use Cases: the portfolio is a section; every deeper path is one record.
  // The detail page's tab is NOT a crumb — the tab strip already names it.
  if (path === "/use-cases" || path.startsWith("/use-cases/")) {
    const isDetail = path.startsWith("/use-cases/");
    if (!isDetail) return [{ label: "Control" }, { label: "Use Cases" }];
    return [
      { label: "Control" },
      { label: "Use Cases", url: "/use-cases" },
      { label: rec || "Use case" },
    ];
  }

  // ---------------- Plan ----------------
  if (path === "/budgets" || path.startsWith("/budgets/")) {
    const isDetail = path.startsWith("/budgets/") && path !== "/budgets/new";
    if (!isDetail) return [{ label: "Plan" }, { label: "Budgets" }];
    return [{ label: "Plan" }, { label: "Budgets", url: "/budgets" }, { label: rec || "Budget" }];
  }
  if (path === "/forecast-lab") {
    return [{ label: "Plan" }, { label: "Forecasts" }];
  }

  // ---------------- Decisions ----------------
  if (path === "/approvals" || path.startsWith("/approvals/")) {
    const isDetail = path.startsWith("/approvals/");
    if (!isDetail) return [{ label: "Decisions" }, { label: "Approvals" }];
    return [{ label: "Decisions" }, { label: "Approvals", url: "/approvals" }, { label: rec || "Approval" }];
  }
  if (path.startsWith("/control-policies/") || path.startsWith("/budget-enforcement/policies")) {
    // The editor routes (/…/:id or /…/new) sit one record deep; append the
    // record name (or "New policy") when we're on one, else just the section.
    const last = seg(path, path.split("/").length - 1);
    const listLeaves = ["approval-policies", "delegations", "policies"];
    const onEditor = !listLeaves.includes(last);
    if (onEditor) {
      return [{ label: "Decisions" }, { label: "Policies & Guardrails" }, { label: rec || (last === "new" ? "New policy" : "Policy") }];
    }
    return [{ label: "Decisions" }, { label: "Policies & Guardrails" }];
  }

  // ---------------- Narrative ----------------
  if (path === "/board-reports" || path.startsWith("/board-reports/")) {
    const reportId = seg(path, 2);
    const isDetail = path.startsWith("/board-reports/") && reportId !== "" && reportId !== "new";
    if (!isDetail) return [{ label: "Narrative" }, { label: "Board Reports" }];
    return [{ label: "Narrative" }, { label: "Board Reports", url: "/board-reports" }, { label: rec || "Report" }];
  }
  if (path === "/investigations" || path.startsWith("/investigations/")) {
    const isDetail = path.startsWith("/investigations/");
    if (!isDetail) return [{ label: "Narrative" }, { label: "Investigations" }];
    return [{ label: "Narrative" }, { label: "Investigations", url: "/investigations" }, { label: rec || "Investigation" }];
  }
  if (path === "/analysis" || path.startsWith("/analysis/")) {
    const isDetail = path.startsWith("/analysis/");
    if (!isDetail) return [{ label: "Narrative" }, { label: "Analysis" }];
    return [{ label: "Narrative" }, { label: "Analysis", url: "/analysis" }, { label: rec || "Analysis" }];
  }
  if (path === "/report-list" || path.startsWith("/report-editor")) {
    if (path === "/report-list") return [{ label: "Narrative" }, { label: "Custom Report" }];
    return [{ label: "Narrative" }, { label: "Custom Report", url: "/report-list" }, { label: rec || "Report" }];
  }

  // ---------------- Settings · Financial model ----------------
  // These pages now live in the dedicated Settings shell, so their first crumb is
  // "Settings" (was "Setup").
  if (path === "/allocation" || path.startsWith("/allocation/") || path === "/detected-sources") {
    const isSourceDetail = path.startsWith("/allocation/sources/");
    if (isSourceDetail) return [{ label: "Settings" }, { label: "Cost Sources", url: "/allocation" }, { label: rec || "Source" }];
    return [{ label: "Settings" }, { label: "Cost Sources" }];
  }
  if (path === "/cost-allocation/hierarchy" || path === "/virtual-tags") {
    return [{ label: "Settings" }, { label: "Allocation Hierarchy" }];
  }
  if (path.startsWith("/cost-source-taxonomy")) {
    return [{ label: "Settings" }, { label: "Taxonomies" }];
  }
  if (path === "/source-connections" || path.startsWith("/source-connections/")) {
    const isDetail = path.startsWith("/source-connections/");
    if (!isDetail) return [{ label: "Settings" }, { label: "Data Connections" }];
    return [{ label: "Settings" }, { label: "Data Connections", url: "/source-connections" }, { label: rec || "Connection" }];
  }

  // ---------------- Optimize (operational, main app) ----------------
  // These stayed in the main app under the renamed "Optimize" sidebar group.
  if (path === "/commitments") return [{ label: "Optimize" }, { label: "Commitments" }];
  if (path === "/optimization-programme") return [{ label: "Optimize" }, { label: "Optimization Programme" }];

  // ---------------- Settings · Access, data & system ----------------
  // Moved into the dedicated Settings shell; first crumb is "Settings" (was "Admin").
  if (path.startsWith("/settings/users")) {
    const isDetail = path.startsWith("/settings/users/") && path !== "/settings/users/new";
    if (!isDetail) return [{ label: "Settings" }, { label: "Users & Access" }];
    return [{ label: "Settings" }, { label: "Users & Access", url: "/settings/users" }, { label: rec || "User" }];
  }
  if (path.startsWith("/litellm-ingest-tokens")) {
    return [{ label: "Settings" }, { label: "Users & Access" }];
  }
  if (path.startsWith("/settings/roles")) {
    return [{ label: "Settings" }, { label: "Roles & Access" }];
  }
  if (path.startsWith("/data-pipeline") || path.startsWith("/reporting-jobs")) {
    const isJob = path.startsWith("/data-pipeline/") && !path.startsWith("/data-pipeline/log");
    if (isJob) return [{ label: "Settings" }, { label: "Data Pipeline", url: "/data-pipeline" }, { label: rec || "Data Job" }];
    // A log entry is a record under the Activity Log. The parent crumb carries the
    // ?tab=activity url deliberately: with only a detail tab present, TopBar derives the
    // back chevron's destination from this crumb, and /data-pipeline alone would land the
    // user on the Pipeline tab rather than the list they came from.
    if (path.startsWith("/data-pipeline/log/")) {
      return [
        { label: "Settings" },
        { label: "Activity Log", url: "/data-pipeline?tab=activity" },
        { label: rec || "Activity" },
      ];
    }
    return [{ label: "Settings" }, { label: "Data Pipeline" }];
  }
  if (path.startsWith("/provider-mappings") || path.startsWith("/application-parameters")) {
    return [{ label: "Settings" }, { label: "System Configuration" }];
  }
  if (path === "/invoice-review" || path.startsWith("/invoice-review/")) {
    const isDetail = path.startsWith("/invoice-review/") && path !== "/invoice-review/settings";
    if (path === "/invoice-review/settings") {
      return [{ label: "Decisions" }, { label: "Invoices", url: "/invoice-review" }, { label: "Automation settings" }];
    }
    if (!isDetail) return [{ label: "Decisions" }, { label: "Invoices" }];
    return [{ label: "Decisions" }, { label: "Invoices", url: "/invoice-review" }, { label: rec || "Invoice" }];
  }
  if (path.startsWith("/invoice-inbound-tokens")) {
    return [{ label: "Settings" }, { label: "Invoice Ingestion" }];
  }
  if (path.startsWith("/settings/ai-subscription")) {
    return [{ label: "Settings" }, { label: "AI Subscription Plan" }];
  }

  return [];
}
